/**
 * Catalogue reads.
 *
 * These hit the **public** endpoints, which is a temporary state worth being
 * explicit about: `GET /v1/admin/products` does not exist yet (ADMIN.md §9,
 * phase 5a). Until it does, the panel can list and inspect products but cannot
 * see the two things an operator most needs — the exact `stockQuantity` and
 * anything in `DRAFT` or `ARCHIVED`, both of which the public surface withholds
 * on purpose (PLAN.md §4.2.1).
 *
 * So this file is scaffolding that proves the pipe end to end. When the admin
 * endpoints land, the paths change and the callers do not.
 */
import { apiFetch } from "@/lib/api-client";
import type { ApiCategory, ApiProduct, ApiResponse, Paginated, SortMode } from "@/lib/api/types";

/** Denser than the storefront's 9 — this is a table, not a showcase grid. */
export const PER_PAGE = 25;

/**
 * The API's own ceiling on `limit`. Asking for more is a 400, not a bigger page.
 */
export const MAX_PAGE_SIZE = 60;

const PUBLIC = { skipAuth: true } as const;

export type ProductQuery = {
  category?: string | undefined;
  search?: string | undefined;
  sort?: SortMode | undefined;
  page?: number | undefined;
  limit?: number | undefined;
};

export function fetchProducts(
  query: ProductQuery = {},
  signal?: AbortSignal,
): Promise<Paginated<ApiProduct[]>> {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: Math.min(query.limit ?? PER_PAGE, MAX_PAGE_SIZE),
  };

  if (query.category) params["category"] = query.category;
  if (query.sort) params["sort"] = query.sort;

  return apiFetch<Paginated<ApiProduct[]>>("/v1/products", {
    ...PUBLIC,
    query: params,
    ...(signal ? { signal } : {}),
  });
}

export async function fetchProduct(slug: string, signal?: AbortSignal): Promise<ApiProduct> {
  const { data } = await apiFetch<ApiResponse<ApiProduct>>(
    `/v1/products/${encodeURIComponent(slug)}`,
    { ...PUBLIC, ...(signal ? { signal } : {}) },
  );

  return data;
}

export async function fetchCategories(signal?: AbortSignal): Promise<ApiCategory[]> {
  const { data } = await apiFetch<ApiResponse<ApiCategory[]>>("/v1/categories", {
    ...PUBLIC,
    ...(signal ? { signal } : {}),
  });

  return data;
}
